import React, {Dispatch, FC, SetStateAction, useMemo, useState} from "react";
import moment from "moment/moment";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import {getWeek} from "../../util/getWeekNumber";


export const SingleSelectCalendar: FC<{taskDate: Date, selected: Date|null, setSelected: Dispatch<SetStateAction<Date|null>>}> = props => {
  function select(d: Date) {
    props.setSelected(d)
  }
  return <SelectCalendar taskDate={props.taskDate} selected={props.selected ? [props.selected] : []} onSelect={select} />
}
export const MultiSelectCalendar: FC<{taskDate: Date, selected: Date[], setSelected: Dispatch<SetStateAction<Date[]>>}> = props => {
  function select(d: Date) {
    props.setSelected((old) => {
      const wasSelected = old.find(o => o.toDateString() === d.toDateString())
      if (wasSelected) {
        return old.filter(o => o.toDateString() !== d.toDateString())
      }
      return [...old, d]
    })
  }
  return <SelectCalendar taskDate={props.taskDate} selected={props.selected} onSelect={select} />
}

export const RangeSelectCalender: FC<{ from: Date|null, setFrom: Dispatch<SetStateAction<Date|null>>, to: Date|null, setTo: Dispatch<SetStateAction<Date|null>>, highlightedDays?: Date[], onRangeSelected?: (from: Date, to: Date) => void, confirmedRanges?: { from: Date, to: Date }[] }> = (props) => {
  const [selectedRange, setSelectedRange] = useState<Date[]>(props.from ? [props.from] : [])

  function getDatesBetween(start: Date, end: Date) {
    const dates: Date[] = []

    const current = new Date(start)

    while (current <= end) {
      dates.push(new Date(current))
      current.setDate(current.getDate() + 1)
    }

    return dates
  }

  function select(d: Date) {
    if (selectedRange.length !== 1) {
      setSelectedRange([d])
      props.setFrom(d)
      props.setTo(d)
      return
    }

    const start = selectedRange[0]
    const from = start < d ? start : d
    const to = start < d ? d : start

    if (props.onRangeSelected) {
      props.onRangeSelected(from, to)
      setSelectedRange([])
      props.setFrom(null)
      props.setTo(null)
      return
    }

    const range = getDatesBetween(from, to)
    setSelectedRange(range)
    props.setFrom(from)
    props.setTo(to)
  }

  return <SelectCalendar taskDate={selectedRange[0] ?? props.from ?? new Date()} selected={selectedRange} onSelect={select} highlightedDates={props.highlightedDays} confirmedRanges={props.confirmedRanges} shouldDisableToday={false}/>
}


const SelectCalendar: FC<{taskDate: Date, selected: Date[], onSelect: (date: Date) => void, highlightedDates?: Date[], confirmedRanges?: { from: Date, to: Date }[], shouldDisableToday?: boolean}> = ({taskDate, selected, onSelect, highlightedDates, confirmedRanges, shouldDisableToday = true}) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date(taskDate.getFullYear(), taskDate.getMonth(), 1, 12))

  function nextMonth() {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1, 12))
  }
  function prevMonth() {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1, 12))
  }

  const options = useMemo(() => {
    const options: Date[] = []
    const firstDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1, 12)
    const lastDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 12)
    for (let i = -firstDay.getDay()+1; i <= lastDay.getDate(); i++) {
      options.push(moment(firstDay).add(i, 'day').toDate())
    }
    return options
  }, [selectedMonth])

  return <div className={' w-80 rounded-2xl bg-white border border-slate-200 z-50'}>
    <div className={'border-t border-slate-100 px-2 py-3'}>
      <div className={"w-full flex items-center justify-between"}>
        <button type={'button'} className={"h-8 w-8 rounded-full hover:bg-slate-200"} onClick={() => prevMonth()}><FontAwesomeIcon icon={faChevronLeft} /></button>
        <span>{ selectedMonth.toLocaleDateString('nl-NL', selectedMonth.getFullYear() === new Date().getFullYear() ? {month: "long"} : {month: "long", year: "numeric"}) }</span>
        <button type={'button'} className={"h-8 w-8 rounded-full hover:bg-slate-200"} onClick={() => nextMonth()}><FontAwesomeIcon icon={faChevronRight} /></button>
      </div>
      <div className={"grid grid-cols-8 mt-2"}>
        <div className={"text-xs text-slate-500 border-r border-slate-100 text-center"}>#</div>
        <div className={"text-xs text-slate-500 text-center"}>Ma</div>
        <div className={"text-xs text-slate-500 text-center"}>Di</div>
        <div className={"text-xs text-slate-500 text-center"}>Wo</div>
        <div className={"text-xs text-slate-500 text-center"}>Do</div>
        <div className={"text-xs text-slate-500 text-center"}>Vr</div>
        <div className={"text-xs text-slate-500 text-center"}>Za</div>
        <div className={"text-xs text-slate-500 text-center"}>Zo</div>
        {options.map((option, index) => {
          if (option.getMonth() !== selectedMonth.getMonth()) {
            return<div key={index} className={option.getDay() === 1 ? "col-span-2" : ""}/>
          }

          const isToday = option.toDateString() === taskDate.toDateString()
          const isSunday = option.getDay() === 0
          const isSelected = selected.some((o) => o.toDateString() === option.toDateString())
          const disabled = isSunday || (shouldDisableToday && isToday)
          const sortedSelected = [...selected].sort((a, b) => a.getTime() - b.getTime())

          const isInRange = sortedSelected.some((s) => s.toDateString() === option.toDateString())
          const isSingle = sortedSelected.length === 1 && isSelected
          const isStart = isInRange && sortedSelected[0]?.toDateString() === option.toDateString()
          const isEnd = isInRange && sortedSelected[sortedSelected.length - 1]?.toDateString() === option.toDateString()
          const isHighlighted = highlightedDates?.some((d) => d.toDateString() === option.toDateString())

          const confirmedRange = confirmedRanges?.find((range) => {
            const from = new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate())
            const to = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate())
            const day = new Date(option.getFullYear(), option.getMonth(), option.getDate())
            return day >= from && day <= to
          })
          const isConfirmed = confirmedRange !== undefined
          const isConfirmedSingle = isConfirmed && confirmedRange!.from.toDateString() === confirmedRange!.to.toDateString()
          const isConfirmedStart = isConfirmed && confirmedRange!.from.toDateString() === option.toDateString()
          const isConfirmedEnd = isConfirmed && confirmedRange!.to.toDateString() === option.toDateString()

          const base = "relative w-full h-9 flex items-center justify-center text-sm transition"
          const hover = disabled ? "text-slate-400" : "hover:bg-slate-100"
          const textColor = isInRange || isSelected ? "text-blue-900" : isConfirmed ? "text-emerald-900" : ""
          const border = isToday ? "border border-blue-900" : "border border-transparent"
          const shape = isSingle
            ? "rounded-full"
            : isStart
              ? "rounded-l-full"
              : isEnd
                ? "rounded-r-full"
                : isInRange
                  ? "rounded-none"
                  : "rounded-full"
          const confirmedShape = isConfirmedSingle
            ? "rounded-full"
            : isConfirmedStart
              ? "rounded-l-full"
              : isConfirmedEnd
                ? "rounded-r-full"
                : "rounded-none"
          const rangeBg = isInRange ? "bg-blue-200" : ""
          const button = <button type={"button"} disabled={disabled} onClick={() => onSelect(option)} className={`${base} ${hover} ${textColor} ${border}`}>
              {isConfirmed && <span className={`absolute inset-0 bg-emerald-100 ${confirmedShape}`}/>}
              {isInRange && <span className={`absolute inset-0 ${rangeBg} ${shape}`}/>}
              <span className={"relative z-10"}>{option.getDate()}</span>
              {isHighlighted && <span className={"absolute bottom-0.5 w-1.5 h-1.5 bg-blue-800 rounded-full"} />}
            </button>

          if (option.getDay() === 1) {
            return <div key={index} className={"col-span-2 grid grid-cols-2 gap-x-1"}>
                <div className={"text-center flex items-center justify-center text-xs font-light text-slate-500 border-r border-slate-100"}>
                  {getWeek(option)}
                </div>
                <div>{button}</div>
              </div>
          }
          return button
        })}
      </div>
    </div>
  </div>
}